// Native drag-and-drop for the COMMAND sidebar, shared by the page's left
// panel and the global Talk to Command drawer. Three kinds of drag, told apart
// by their MIME type so one row can accept more than one:
//   • a chat onto a project row (or Other chats) — moves it, same store action
//     as the ⋯ menu;
//   • a project onto another project row — reorders the folders;
//   • an agent onto another agent row — reorders My agents.
// Native HTML5 DnD (not @hello-pangea/dnd) because the chat targets are folder
// rows, not list positions — chat order inside a group is derived from
// updatedAt.

import type { DragEvent, Dispatch, SetStateAction } from "react";

const CHAT_DRAG_MIME = "application/x-aios-command-chat";
const PROJECT_DRAG_MIME = "application/x-aios-command-project";
const AGENT_DRAG_MIME = "application/x-aios-command-agent";

/** Key for the "Other chats" drop target in the panel-level drag-over state. */
export const OTHER_CHATS_DROP_KEY = "__other-chats__";

export function setChatDragData(event: DragEvent, sessionId: string) {
  event.dataTransfer.setData(CHAT_DRAG_MIME, sessionId);
  event.dataTransfer.effectAllowed = "move";
}

export function isChatDrag(event: DragEvent) {
  return event.dataTransfer.types.includes(CHAT_DRAG_MIME);
}

export function setProjectDragData(event: DragEvent, projectId: string) {
  event.dataTransfer.setData(PROJECT_DRAG_MIME, projectId);
  event.dataTransfer.effectAllowed = "move";
}

export function isProjectDrag(event: DragEvent) {
  return event.dataTransfer.types.includes(PROJECT_DRAG_MIME);
}

export function setAgentDragData(event: DragEvent, agentId: string) {
  event.dataTransfer.setData(AGENT_DRAG_MIME, agentId);
  event.dataTransfer.effectAllowed = "move";
}

export function isAgentDrag(event: DragEvent) {
  return event.dataTransfer.types.includes(AGENT_DRAG_MIME);
}

/**
 * Fallback for the area around a reorderable list — for projects that's the
 * chat lists nested under open folders and the gaps between them. Without it,
 * releasing a dragged row anywhere but exactly on another row would cancel
 * the reorder and snap the list back to where it started.
 */
export function reorderListDropHandlers(options: {
  kind: "project" | "agent";
  onDropItem: (itemId: string) => void;
}) {
  const mime = options.kind === "agent" ? AGENT_DRAG_MIME : PROJECT_DRAG_MIME;
  const matches = (event: DragEvent) => event.dataTransfer.types.includes(mime);
  return {
    onDragOver: (event: DragEvent) => {
      if (!matches(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    onDrop: (event: DragEvent) => {
      if (!matches(event)) return;
      event.preventDefault();
      event.stopPropagation();
      const itemId = event.dataTransfer.getData(mime);
      if (itemId) options.onDropItem(itemId);
    },
  };
}

/**
 * Handlers for an agent row. Agents only accept agent drags — dropping a chat
 * on one would have no meaning — so this is the plain reorder pair.
 */
export function agentRowDropHandlers(options: {
  agentId: string;
  /** fires while a dragged agent is held over this row */
  onAgentDragOver: (targetAgentId: string) => void;
  onDropAgent: (agentId: string) => void;
}) {
  return {
    onDragOver: (event: DragEvent) => {
      if (!isAgentDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      options.onAgentDragOver(options.agentId);
    },
    onDrop: (event: DragEvent) => {
      if (!isAgentDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      const agentId = event.dataTransfer.getData(AGENT_DRAG_MIME);
      if (agentId) options.onDropAgent(agentId);
    },
  };
}

/**
 * Handlers for a project row, which accepts BOTH drags: a chat dropped on it
 * moves into the project, a project dropped on it takes this row's slot. One
 * combined set rather than two spreads, because a single element can only
 * carry one onDragOver/onDrop pair.
 *
 * The two drags never highlight the same way — a chat drop fills the row, a
 * project drop previews the reorder in place — so they track separate state.
 */
export function projectRowDropHandlers(options: {
  /** this row's project id — the chat-drop highlight key and the reorder target */
  projectId: string;
  setDragOver: Dispatch<SetStateAction<string | null>>;
  onDropChat: (sessionId: string) => void;
  /** fires while a dragged project is held over this row */
  onProjectDragOver: (targetProjectId: string) => void;
  onDropProject: (projectId: string) => void;
}) {
  return {
    onDragOver: (event: DragEvent) => {
      if (isProjectDrag(event)) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        options.onProjectDragOver(options.projectId);
        return;
      }
      if (!isChatDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      options.setDragOver(options.projectId);
    },
    onDragLeave: (event: DragEvent) => {
      // ignore leaves into our own children; only clear if we still own the highlight
      if (
        event.relatedTarget instanceof Node &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }
      options.setDragOver((current) =>
        current === options.projectId ? null : current
      );
    },
    onDrop: (event: DragEvent) => {
      if (isProjectDrag(event)) {
        event.preventDefault();
        event.stopPropagation();
        const projectId = event.dataTransfer.getData(PROJECT_DRAG_MIME);
        if (projectId) options.onDropProject(projectId);
        return;
      }
      if (!isChatDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      options.setDragOver(null);
      const sessionId = event.dataTransfer.getData(CHAT_DRAG_MIME);
      if (sessionId) options.onDropChat(sessionId);
    },
  };
}

/**
 * Handlers for one drop target (a project row/list or the Other chats block).
 * `key` identifies the target in the panel-level drag-over state so only the
 * hovered target highlights; `onDropChat` receives the dragged session id.
 */
export function chatDropHandlers(options: {
  key: string;
  setDragOver: Dispatch<SetStateAction<string | null>>;
  onDropChat: (sessionId: string) => void;
}) {
  return {
    onDragOver: (event: DragEvent) => {
      if (!isChatDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      options.setDragOver(options.key);
    },
    onDragLeave: (event: DragEvent) => {
      // ignore leaves into our own children; only clear if we still own the highlight
      if (
        event.relatedTarget instanceof Node &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }
      options.setDragOver((current) => (current === options.key ? null : current));
    },
    onDrop: (event: DragEvent) => {
      if (!isChatDrag(event)) return;
      event.preventDefault();
      event.stopPropagation();
      options.setDragOver(null);
      const sessionId = event.dataTransfer.getData(CHAT_DRAG_MIME);
      if (sessionId) options.onDropChat(sessionId);
    },
  };
}
